import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { BankAckView, BankChannelView, PollResult } from '../types'

const BASE = '/v1/payment/bank-channels'

export function useBankChannels() {
  return useQuery({
    queryKey: ['bank-channels'],
    queryFn: async () => (await apiClient.get<BankChannelView[]>(BASE)).data,
  })
}

export function useBankAcknowledgements(channelId?: string) {
  return useQuery({
    queryKey: ['bank-channels', 'acks', channelId ?? 'all'],
    queryFn: async () => (await apiClient.get<BankAckView[]>(`${BASE}/acknowledgements`, {
      params: channelId ? { channelId } : undefined,
    })).data,
  })
}

export function useBankChannelMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bank-channels'] })

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await apiClient.post<BankChannelView>(BASE, body)).data,
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await apiClient.put<BankChannelView>(`${BASE}/${id}`, body)).data,
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`${BASE}/${id}`) },
    onSuccess: invalidate,
  })

  const pollNow = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<PollResult>(`${BASE}/${id}/poll`)).data,
    onSuccess: invalidate,
  })

  return { create, update, remove, pollNow }
}
